import { type ReactNode } from 'react';
import { type CrescimentoCliente } from '@varga/shared';

/**
 * `TrilhaBrindes` — a régua (DESIGN-FOUNDATION §5.4 / §6.2). Não é stepper: um
 * trilho de 2px com quatro marcas no vocabulário de escala de instrumento.
 * O marcador atual é **vazado** de propósito (progresso falso é proibido);
 * vermelho preenchido na régua só depois que a indicação converteu.
 *
 * **Tudo vem de `GET /me`** (`nivelAtual`, `proximoBrinde`, `faltamParaProximo`,
 * `cicloReiniciou`, `totalConvertidas`) — nenhum cálculo de nível no cliente
 * (CLAUDE.md regra 1 e 3).
 */
export function TrilhaBrindes({ crescimento }: { crescimento: CrescimentoCliente }): ReactNode {
  const { nivelAtual, proximoBrinde, faltamParaProximo, cicloReiniciou, totalConvertidas } =
    crescimento;

  // Campanha encerrada (sem próximo brinde): melhor ausência que régua mentindo (§6.2).
  if (!proximoBrinde) return null;

  const niveis = [1, 2, 3, 4];
  const primeiraIndicacao = totalConvertidas === 0;
  const resetRecente = cicloReiniciou && nivelAtual === 1;

  const distancia =
    faltamParaProximo <= 1
      ? 'Falta 1 indicação para você ganhar.'
      : `Faltam ${faltamParaProximo} indicações para você ganhar.`;

  return (
    <section aria-label="Trilha de brindes">
      <div className="relative h-[72px]">
        {/* trilho — termina a 96px da borda (`right-24`/`pr-24`), largura que
            comporta o rótulo "RECOMEÇA" (medido ~74-76px em D-DIN Exp e no
            fallback do sistema) sem invadir a marca do nível 4 */}
        <div className="absolute left-1.5 right-24 top-[15px] h-0.5 bg-linha-forte" />
        <ol className="relative flex justify-between pr-24">
          {niveis.map((n) => (
            <li key={n} className="flex flex-col items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center">
                <Marca estado={n < nivelAtual ? 'feito' : n === nivelAtual ? 'atual' : 'futuro'} />
              </span>
              <span className="tipo-rotulo-xs text-tinta-3">{n}</span>
            </li>
          ))}
        </ol>
        {/* arco RECOMEÇA — desenho honesto do módulo 4 (§5.4). Largura fixa
            (`w-24`, igual ao espaço reservado acima) e alinhado à esquerda
            (`items-start`) faz o arco começar exatamente onde o trilho termina
            — e `whitespace-nowrap` garante que o rótulo nunca quebra ou estoura
            sobre a marca do nível 4, mesmo no fallback de fonte antes da D-DIN
            Exp carregar (medido ~74-76px, contra os 44px antigos do `pr-11`). */}
        <div className="absolute right-0 top-0 flex w-24 flex-col items-start">
          <svg width="36" height="32" viewBox="0 0 36 32" fill="none" aria-hidden>
            <path
              d="M2 15 H20 a10 10 0 0 1 0 20 M18 31 l4 4 l-4 4"
              stroke="var(--linha-forte)"
              strokeWidth="2"
              strokeLinecap="round"
              transform="translate(0 -8)"
            />
          </svg>
          <span className="tipo-rotulo-xs mt-1 whitespace-nowrap text-tinta-3">Recomeça</span>
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-1">
        {resetRecente && (
          <p className="tipo-corpo text-tinta">
            <span className="tipo-corpo-forte text-tinta-forte">
              Você já ganhou {totalConvertidas} {totalConvertidas === 1 ? 'brinde' : 'brindes'}.
            </span>{' '}
            Um novo ciclo começou.
          </p>
        )}
        <p className="tipo-rotulo text-tinta-2">Próximo brinde</p>
        <p className="tipo-display text-tinta-forte">{proximoBrinde}</p>
        <p className="tipo-corpo text-tinta">
          {primeiraIndicacao ? 'É o que você ganha na sua primeira indicação.' : distancia}
        </p>
      </div>
    </section>
  );
}

function Marca({ estado }: { estado: 'feito' | 'atual' | 'futuro' }): ReactNode {
  if (estado === 'feito') {
    return <span className="block h-2.5 w-2.5 bg-tinta-forte" aria-label="nível conquistado" />;
  }
  if (estado === 'atual') {
    return (
      <span
        className="block h-3.5 w-3.5 border-2 border-acento bg-fundo shadow-[0_0_0_4px_var(--acento-fraco)]"
        aria-label="nível atual"
      />
    );
  }
  return <span className="block h-2 w-0.5 bg-linha-forte" aria-label="nível futuro" />;
}
